import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, DollarSign, GitMerge, X } from "lucide-react";
import { mergeEvents } from "@/lib/api";
import { toast } from "sonner";
import { ManualLinkPicker } from "./ManualLinkPicker";

export function ReviewTabContent({
  mergeSuggestions, setMergeSuggestions,
  orphanQBEvents, unmatchedCounts,
  pendingEvents, unlinkedGcalEvents,
  canEdit, fetchAll, filteredBySearch,
  renderEventCard
}) {
  const onManualMerge = async (gcalEventId, qbEventId) => {
    try {
      await mergeEvents(gcalEventId, qbEventId);
      toast.success("Events linked successfully");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link failed");
      throw err;
    }
  };

  return (
    <div className="mt-6 space-y-6">
      {/* Summary bar */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>{unmatchedCounts.qb} QB estimate{unmatchedCounts.qb !== 1 ? 's' : ''} unlinked</span>
        <span className="text-border">|</span>
        <span>{mergeSuggestions.length} merge suggestion{mergeSuggestions.length !== 1 ? 's' : ''}</span>
        <span className="text-border">|</span>
        <span>{pendingEvents.length} pending event{pendingEvents.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Section 1: Merge Suggestions */}
      {mergeSuggestions.length > 0 && (
        <div data-testid="merge-suggestions" className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
            <GitMerge className="w-3.5 h-3.5" /> Merge Suggestions — Link QB Estimates to Calendar Events
          </p>
          {mergeSuggestions.map((s, i) => (
            <Card key={`${s.gcal_event?.event_id}-${s.qb_event?.event_id}`} data-testid={`merge-suggestion-${i}`} className="border-amber-500/20 bg-amber-500/5">
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-400">Calendar</Badge>
                      <span className="text-sm font-medium truncate">{s.gcal_event.name}</span>
                      <span className="text-xs text-muted-foreground">{s.gcal_event.start_date}</span>
                      <Badge variant="outline" className={`text-xs status-${s.gcal_event.status}`}>{s.gcal_event.status}</Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-400">QuickBooks</Badge>
                      <span className="text-sm font-medium truncate">{s.qb_event.name}</span>
                      <span className="text-xs text-muted-foreground">{s.qb_event.start_date}</span>
                      {s.qb_event.revenue > 0 && <span className="text-xs text-emerald-400 font-semibold">${s.qb_event.revenue?.toLocaleString()}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge variant="secondary" className={`text-xs ${s.confidence === 'high' ? 'bg-emerald-500/10 text-emerald-400' : s.confidence === 'medium' ? 'bg-amber-500/10 text-amber-400' : 'bg-zinc-500/10 text-zinc-400'}`}>
                        {s.confidence} match ({Math.round(s.similarity * 100)}%)
                      </Badge>
                      {s.date_match && <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-400">Same date</Badge>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      data-testid={`merge-btn-${i}`}
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={async () => {
                        try {
                          await mergeEvents(s.gcal_event.event_id, s.qb_event.event_id);
                          toast.success(`Merged "${s.qb_event.name}" into "${s.gcal_event.name}"`);
                          fetchAll();
                          setMergeSuggestions(prev => prev.filter((_, idx) => idx !== i));
                        } catch (err) {
                          toast.error("Merge failed");
                        }
                      }}
                    >
                      <GitMerge className="w-3.5 h-3.5 mr-1.5" /> Merge
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs text-muted-foreground"
                      onClick={() => setMergeSuggestions(prev => prev.filter((_, idx) => idx !== i))}
                    >
                      <X className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Section 2: Orphan QB Estimates */}
      {orphanQBEvents.length > 0 && (
        <div data-testid="orphan-qb-events" className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-green-400 flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" /> Unlinked QB Estimates — No Calendar Match
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {orphanQBEvents.map((ev) => (
              <Card key={ev.event_id} data-testid={`orphan-qb-${ev.event_id}`} className="border-green-500/15 bg-green-500/5">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{ev.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{ev.client_name} &middot; {ev.start_date}</p>
                      {ev.revenue > 0 && <p className="text-xs text-emerald-400 font-semibold mt-0.5">${ev.revenue?.toLocaleString()}</p>}
                    </div>
                    <Badge variant="outline" className={`text-xs shrink-0 status-${ev.status}`}>{ev.status}</Badge>
                  </div>
                  {canEdit && (
                    <ManualLinkPicker
                      qbEvent={ev}
                      gcalEvents={unlinkedGcalEvents}
                      onMerge={onManualMerge}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Section 3: Pending events */}
      {pendingEvents.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-400 flex items-center gap-1.5 mb-2">
            <Clock className="w-3.5 h-3.5" /> Pending Confirmation
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredBySearch(pendingEvents).map(ev => renderEventCard(ev))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {mergeSuggestions.length === 0 && orphanQBEvents.length === 0 && pendingEvents.length === 0 && (
        <div className="text-center py-10">
          <Clock className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">All caught up</p>
          <p className="text-xs text-muted-foreground/60 mt-1">No events need review. New QB estimates and calendar events will appear here.</p>
        </div>
      )}
    </div>
  );
}
